import { useRef, useEffect, useState } from 'react';
import styles from '../index.module.scss';
import Card from '../Card';
import { Rewiew } from '..';

interface InfiniteSliderProps {
  reviews: Rewiew[];
}

type Direction = 'left' | 'right';

export default function InfiniteSlider({ reviews }: InfiniteSliderProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState(reviews);
  const [cardWidth, setCardWidth] = useState(0);
  const [paused, setPaused] = useState(false);
  const [direction, setDirection] = useState<Direction>('right');

  useEffect(() => {
    if (cardRef.current) {
      setCardWidth(cardRef.current.offsetWidth + 20);
    }
  }, []);

  useEffect(() => {
    if (!cardWidth) return;
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    let frameId: number;

    const step = () => {
      if (!paused) {
        if (direction === 'right') {
          wrapper.scrollLeft += 0.5;

          if (wrapper.scrollLeft >= cardWidth) {
            wrapper.scrollLeft -= cardWidth;
            setItems((prev) => [...prev.slice(1), prev[0]]);
          }
        } else {
          wrapper.scrollLeft -= 0.5;

          if (wrapper.scrollLeft <= 0) {
            wrapper.scrollLeft += cardWidth;
            setItems((prev) => {
              const last = prev[prev.length - 1];
              return [last, ...prev.slice(0, -1)];
            });
          }
        }
      }

      frameId = requestAnimationFrame(step);
    };

    frameId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameId);
  }, [cardWidth, paused, direction]);

  const handleScroll = () => {
    const wrapper = wrapperRef.current;
    if (!wrapper || !cardWidth) return;

    if (direction === 'right' && wrapper.scrollLeft >= cardWidth) {
      wrapper.scrollLeft -= cardWidth;
      setItems((prev) => [...prev.slice(1), prev[0]]);
    } else if (direction === 'left' && wrapper.scrollLeft <= 0) {
      wrapper.scrollLeft += cardWidth;
      setItems((prev) => {
        const last = prev[prev.length - 1];
        return [last, ...prev.slice(0, -1)];
      });
    }
  };

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '1280px',
        margin: '0 auto',
        overflow: 'hidden',
      }}
    >
      <div
        ref={wrapperRef}
        onScroll={handleScroll}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        style={{
          overflowX: 'auto',
          display: 'flex',
          scrollBehavior: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {items.map((review, index) => (
          <div
            key={index}
            ref={index === 0 ? cardRef : null}
            style={{ flex: '0 0 auto', marginRight: 20 }}
          >
            <Card review={review} />
          </div>
        ))}
      </div>
    </div>
  );
}
